import { DRIVER_TEMPLATE_VARIABLE_KEYS } from '@/features/load-hub/components/expanded-row/savedTemplate'

type EmailTemplateVariableCategory = 'Route' | 'Load' | 'Pricing' | 'Broker' | 'Company' | 'Dispatcher' | 'Driver'

interface EmailTemplateVariableDefinition {
    key: string
    label: string
    category: EmailTemplateVariableCategory
    /** What the field holds, in words an AI assistant can choose by — see MEANINGS. */
    meaning: string
}

/**
 * The ONE list of template fields. The variable picker, the editor's unknown-variable check, the preview
 * values and the AI prompt are all derived from it, so a field added here reaches every one of them.
 *
 * Adding a field takes three things, and the first two fail loudly when forgotten:
 *  1. Its key here, in the group whose data it is.
 *  2. Its MEANING below — `MEANINGS` is keyed by these groups, so the compiler rejects a field without one.
 *     The AI prompt sends only meanings; a bare key is how an assistant came to put the broker's MC
 *     ({{mc_number}}) where the sender's ({{mc}}) belonged, or to hardcode values it could not identify.
 *  3. A value: `applyTemplateVariables` / `buildUserReplacements` (load, broker, company) or
 *     `buildDriverTemplateVariables` (driver). `emailTemplateVariables.test.ts` fails for a field nothing
 *     fills — otherwise it would reach the broker as raw {{braces}}.
 */
const GROUPS = {
    Route: [
        'origin',
        'destination',
        'origin_city',
        'origin_state',
        'destination_city',
        'destination_state',
        'pickup_date',
        'date',
        'trip_miles',
        'total_mileage',
        'total_miles',
        'deadhead_mileage',
        'deadhead',
    ],
    Load: ['load_id', 'reference_id', 'equipment', 'equipment_type', 'weight', 'length', 'load_type'],
    Pricing: ['rate', 'total_rate', 'rpm'],
    Broker: ['broker_contact_name', 'broker_email', 'company_name', 'mc_number', 'broker_company', 'broker_mc', 'credit_score', 'days_to_pay'],
    Company: [
        'company',
        'mc',
        'dot',
        'address',
        'phone',
        'transfer',
        'agent_name',
        'notes',
        'company_logo',
        'user_company',
        'user_mc',
        'user_dot',
        'user_phone',
        'user_transfer',
        'user_agent',
        'user_address',
    ],
    Dispatcher: ['user_name', 'user_email', 'phone_line', 'dot_line'],
    Driver: DRIVER_TEMPLATE_VARIABLE_KEYS,
} as const satisfies Record<EmailTemplateVariableCategory, readonly string[]>

type EmailTemplateVariableKey = (typeof GROUPS)[EmailTemplateVariableCategory][number]

/** Whose data each group is. Sent to the AI with the fields, so "our MC" and "the broker's MC" cannot be confused. */
export const EMAIL_TEMPLATE_VARIABLE_CATEGORIES: ReadonlyArray<{ category: EmailTemplateVariableCategory; meaning: string }> = [
    { category: 'Route', meaning: 'the lane of the load being quoted' },
    { category: 'Load', meaning: 'the load being quoted' },
    { category: 'Pricing', meaning: 'your offer for this load' },
    { category: 'Broker', meaning: 'the brokerage RECEIVING this email — never use these for your own company' },
    { category: 'Company', meaning: 'YOUR carrier company, the sender' },
    { category: 'Dispatcher', meaning: 'the dispatcher sending this email' },
    { category: 'Driver', meaning: 'the driver and truck offered for this load; each shows "-" when no driver is picked' },
]

/** What each field holds. Written for the AI prompt: it reads these, not the keys, to pick a field. */
const MEANINGS: Record<EmailTemplateVariableKey, string> = {
    origin: 'Pickup city and state, e.g. "Wichita, KS"',
    destination: 'Delivery city and state, e.g. "West Palm Beach, FL"',
    origin_city: 'Pickup city only',
    origin_state: 'Pickup state only',
    destination_city: 'Delivery city only',
    destination_state: 'Delivery state only',
    pickup_date: 'Pickup date, MM/DD/YYYY',
    date: 'Older alias of the pickup date; prefer {{pickup_date}}',
    trip_miles: 'Loaded miles from pickup to delivery, whole number without "mi"',
    total_mileage: 'Loaded miles as posted, may have decimals; prefer {{trip_miles}}',
    total_miles: 'Same as {{total_mileage}}; prefer {{trip_miles}}',
    deadhead_mileage: "Empty miles to pickup, whole number, 0 when unknown; for the picked driver's distance use {{miles_out}}",
    deadhead: 'Same as {{deadhead_mileage}}',
    load_id: "The load number shown in the app; for the broker's own reference use {{reference_id}}",
    reference_id: "The broker's own posting reference for this load",
    equipment: 'Equipment type the load asks for, e.g. "Box Truck"; empty when unknown',
    equipment_type: 'Same as {{equipment}}, but "N/A" when unknown',
    weight: 'Load weight in lbs, number only',
    length: 'Required equipment length in feet, number only',
    load_type: 'Full or partial load',
    rate: 'Your offer amount formatted with commas and no "$", e.g. "1,100" — write it as ${{rate}}',
    total_rate: 'Your offer amount unformatted, e.g. "1100"; prefer {{rate}}',
    rpm: 'Your offer per loaded mile, e.g. "0.74" — write it as ${{rpm}}/mi',
    broker_contact_name: 'The broker contact\'s name, for a greeting; "Team" when unknown',
    broker_email: 'The broker address this email is sent to',
    company_name: 'The BROKER\'s company name (older alias of {{broker_company}}), "N/A" when unknown — not your company; use {{company}} for yours',
    mc_number: 'The BROKER\'s MC number (older alias of {{broker_mc}}), "N/A" when unknown — not your MC; use {{mc}} for yours',
    broker_company: "The broker's company name; empty when unknown",
    broker_mc: "The broker's MC number; empty when unknown",
    credit_score: 'The broker\'s credit score; "N/A" when unknown',
    days_to_pay: 'The broker\'s average days to pay; "N/A" when unknown',
    company: "Your company's name",
    mc: "Your company's MC number",
    dot: "Your company's USDOT number",
    address: "Your company's address",
    phone: "Your company's phone number",
    transfer: 'Your call-transfer phone number',
    agent_name: "Your AI agent's name",
    notes: "Your company's saved notes text",
    company_logo: 'Your company logo as a complete image (it inserts a whole <img>); place it on its own, never inside an attribute',
    user_company: 'Same as {{company}}',
    user_mc: 'Same as {{mc}}',
    user_dot: 'Same as {{dot}}',
    user_phone: 'Same as {{phone}}',
    user_transfer: 'Same as {{transfer}}',
    user_agent: 'Same as {{agent_name}}',
    user_address: 'Same as {{address}}',
    user_name: "The sending dispatcher's name",
    user_email: 'The email address this email is sent from',
    phone_line: '"Phone: " followed by your company phone, or empty when there is none',
    dot_line: '" · DOT: " followed by your USDOT, or empty — meant to follow the MC on the same line',
    driver_name: "The offered driver's name",
    driver_phone: "The offered driver's phone number",
    driver_equipment: 'The offered driver\'s truck type, e.g. "Small Straight"',
    miles_out: 'Distance from the picked driver to pickup, including "mi", e.g. "141 mi"',
    driver_dimensions: 'The truck\'s cargo space, length×width×height, e.g. "16×8×8"',
    driver_payload: 'The truck\'s maximum payload, e.g. "10,000 lbs"',
    dock_height_ramps: 'Whether the truck is dock height or has ramps',
    equipment_capabilities: 'What the truck carries or can do, comma-separated, e.g. "Lift-Gate, Pallet Jack, Straps"',
    driver_location: "The driver's current city and state",
    truck_number: "The truck's unit number",
}

const LABELS: Record<string, string> = {
    mc: 'MC',
    dot: 'USDOT',
    rpm: 'RPM',
    user_mc: 'Company MC',
    user_dot: 'Company USDOT',
    broker_mc: 'Broker MC',
    mc_number: 'Broker MC number',
    load_id: 'Load ID',
    reference_id: 'Broker reference',
    company_logo: 'Company logo',
    truck_number: 'Truck #',
    dock_height_ramps: 'Dock height / Ramps',
}

const humanize = (key: string): string => LABELS[key] ?? key.replace(/_/g, ' ').replace(/^./, (letter) => letter.toUpperCase())

export const EMAIL_TEMPLATE_VARIABLES: EmailTemplateVariableDefinition[] = EMAIL_TEMPLATE_VARIABLE_CATEGORIES.flatMap(({ category }) =>
    GROUPS[category].map((key: EmailTemplateVariableKey) => ({ key, label: humanize(key), category, meaning: MEANINGS[key] }))
)

const EMAIL_TEMPLATE_VARIABLE_KEYS = new Set(EMAIL_TEMPLATE_VARIABLES.map(({ key }) => key))

const TEMPLATE_VARIABLE_PATTERN = /\{\{\s*([a-zA-Z_][a-zA-Z0-9_]*)\s*\}\}/g

export const findUnknownTemplateVariables = (...sources: string[]): string[] => {
    const unknown = new Set<string>()
    for (const source of sources) {
        for (const match of source.matchAll(TEMPLATE_VARIABLE_PATTERN)) {
            if (!EMAIL_TEMPLATE_VARIABLE_KEYS.has(match[1])) unknown.add(match[1])
        }
    }
    return [...unknown].sort()
}
